import { FormProvider } from "react-hook-form";
import { EyeIcon, Trash2, X } from "lucide-react";
import { FormInput, FormSelect, FormDatePicker, FormCombobox, Select, FormLabel } from "@/components";
import { Button } from "@/components/ui";
import { taskStatus, LOGIN_ROLES, yearOptions } from "@/lib/utils";
import { useProfilesByRoleQuery } from "@/services/employeeManagement";
import { useGetInventoryStatusDetailBySchoolIdQuery } from "@/services/school";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { useEffect, useMemo, useState } from "react";
import useGetChecklistInventory from "@/services/masterData/inventorysetup/useGetChecklistInventory";
import useUpdateChecklistInventoryMutation from "@/services/masterData/inventorysetup/useUpdateChecklistInventory";
import { toast } from "sonner";
import axiosAuth from "@/services/axiosAuth";
import { GET_CHECK_LIST, GET_CHECK_STATUS_LIST } from "@/services";
import moment from "moment";

/* ------------------------------------------------------------------ */
/* TYPES */
/* ------------------------------------------------------------------ */

type ChecklistState = Record<
  string,
  {
    checked: boolean;     // UI state
    locked: boolean;      // came from API
    comment: string;
  }
>;


interface InventoryStatusDetailsProps {
  inventoryStatusForm: any;
  isLoadingInventoryStatus: boolean;
  isLoadingExisting: boolean;
  onSubmitInventoryStatus: (data: any) => void;
  handleDeleteInventoryStatus: (index: number) => void;
}

/* ------------------------------------------------------------------ */
/* COMPONENT */
/* ------------------------------------------------------------------ */

const InventoryStatusDetails = ({
  inventoryStatusForm,
  isLoadingExisting,
  onSubmitInventoryStatus,
  handleDeleteInventoryStatus,
}: InventoryStatusDetailsProps) => {
  const { id } = useParams();
  const schoolId = id || "";

  /* ---------------- Auth ---------------- */
  const roles =
    useSelector(
      (state: RootState) => state.currentData.loggedInUserData?.role
    ) || [];

  const hasRole = (role: string) =>
    Array.isArray(roles) && roles.includes(role);

  const canEditChecklist =
    hasRole(LOGIN_ROLES.INVENTORY_TEAM) || hasRole(LOGIN_ROLES.SUPER_ADMIN);

  /* ---------------- Dialog State ---------------- */
  const [openChecklist, setOpenChecklist] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [mode, setMode] = useState<"edit" | "view" | "editRow">("edit");
  const [activeRowIndex, setActiveRowIndex] = useState<number | null>(null);
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [academicYear, setAcademicYear] = useState<string>("");
  const isViewMode = mode === "view";

  /* ---------------- Checklist Data ---------------- */
  const { data: checklistResp, refetch: refetchChecklist } =
    useGetChecklistInventory(schoolId, {limit, page, ...academicYear ? {academicYear} : {}});

  const totalRecords = (checklistResp?.data?.[0] as any)?.pagination?.[0]?.totalRecords;
  const checklistData = (checklistResp?.data?.[0] as any)?.records || [];

  const [savedChecklistState, setSavedChecklistState] =
    useState<ChecklistState>({});

  const [draftChecklistState, setDraftChecklistState] =
    useState<ChecklistState>({});

  /* ---------------- Inventory Status ---------------- */
  const { data: inventoryStatusResp, refetch: refetchInventoryStatus } =
    useGetInventoryStatusDetailBySchoolIdQuery(schoolId);

  const inventoryChecklistDetails =
    inventoryStatusResp?.data?.inventoryChecklistDetails || [];

  /* ---------------- Init checklist (EDIT MODE) ---------------- */
  useEffect(() => {
    if (!checklistData.length) return;

    const state: ChecklistState = {};
    checklistData.forEach((item: any) => {
      state[item._id] = {
        checked: !!item.included,
        locked: !!item.included,
        comment: item.comments || "-",
      };
    });

    console.log({ checklistData });


    setSavedChecklistState(state);
    setDraftChecklistState(state);
  }, [checklistData]);



  /* ---------------- View mode checklist ---------------- */
  const viewChecklistRows = useMemo(() => {
    if (mode !== "view" || activeRowIndex === null) return [];

    const inventoryCheckListsArr =
      inventoryStatusResp?.data?.inventoryStatusList?.[activeRowIndex]
        ?.inventoryCheckLists || [];

    return inventoryCheckListsArr
  }, [mode, activeRowIndex, inventoryStatusResp, inventoryChecklistDetails]);

  /* ---------------- Reset on close ---------------- */
  useEffect(() => {
    if (!openChecklist) {
      setMode("edit");
      setActiveRowIndex(null);
    }
  }, [openChecklist]);

  /* ---------------- Update checklist ---------------- */
  const updateChecklistMutation = useUpdateChecklistInventoryMutation();

  const handleUpdateChecklist = async () => {
    try {
      const checklistItems = checklistData.map((item: any) => ({
        tlmMasterId: item.tlmMasterId,
        isIncluded: draftChecklistState[item._id]?.checked || false,
        comment: draftChecklistState[item._id]?.comment || "-",
      }));

      await updateChecklistMutation.mutateAsync({
        id: schoolId,
        data: { checklistItems },
      });

      // COMMIT draft → saved
      setSavedChecklistState(draftChecklistState);

      await refetchChecklist();
      await refetchInventoryStatus();

      // close dialogs
      setOpenConfirmDialog(false);
      setOpenChecklist(false);
      toast.success("Checklist updated successfully");

    } catch (error) {
      toast.error("Failed to update checklist");
    }
  };


  useEffect(() => {
    if (!openChecklist) {
      setDraftChecklistState(savedChecklistState);
    }
  }, [openChecklist, savedChecklistState]);

  /* ---------------- Inventory submit ---------------- */
  const buildInventoryCheckLists = () =>
    Object.entries(savedChecklistState)
      .filter(([_, v]) => v.checked)
      .map(([id]) => id);



  const handleInventorySubmit = (formData: any) => {
    console.log({ formData });

    onSubmitInventoryStatus(formData);
    setTimeout(() => refetchChecklist(), 200)
  };

  /* ---------------- Options ---------------- */
  const statusOptions = useMemo(
    () =>
      Object.values(taskStatus).map((s) => ({
        label: s.label,
        value: s.value,
      })),
    []
  );

  const onPrevBtn = () => {
    setPage((prev: number) => prev - 1);

    setTimeout(() => refetchChecklist(), 100)
  }

const onNextBtn = () => {
  setPage((prev: number) => prev + 1)
  setTimeout(() => refetchChecklist(), 100)
}

const handleDownload = async () => {
  
  try {
    const response = await axiosAuth.get(`${GET_CHECK_LIST}${schoolId}/download`,{
      responseType: "blob",
    });

    if (response?.status === 200) {
      // Create blob link and trigger download
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Try to get filename from headers, fallback to fileName or default
      const contentDisposition = response.headers["content-disposition"];
      let downloadFileName = "document.pdf";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) {
          downloadFileName = match[1];
        }
      }

      link.setAttribute("download", downloadFileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } else {
      toast.error("There is an issue while downloading the file");
    }
  } catch (error: any) {
    toast.error(
      error?.response?.data?.message || "There is an issue while downloading the file"
    );
  }
};

const handleChecklistDownload = async () => {
  
  try {
    const response = await axiosAuth.get(`${GET_CHECK_STATUS_LIST}${schoolId}/download-checklist/${inventoryStatusResp?.data?.inventoryStatusList?.[activeRowIndex as any]?._id}`,{
      responseType: "blob",
    });

    if (response?.status === 200) {
      // Create blob link and trigger download
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Try to get filename from headers, fallback to fileName or default
      const contentDisposition = response.headers["content-disposition"];
      let downloadFileName = "document.pdf";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) {
          downloadFileName = match[1];
        }
      }

      link.setAttribute("download", downloadFileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } else {
      toast.error("There is an issue while downloading the file");
    }
  } catch (error: any) {
    toast.error(
      error?.response?.data?.message || "There is an issue while downloading the file"
    );
  }
};

  const { data: labsteamData } =
    useProfilesByRoleQuery(LOGIN_ROLES.INVENTORY_TEAM);

  const backendApprovers =
    inventoryStatusResp?.data?.approvedByDetails?.map((p: any) => ({
      label: p.name,
      value: p.profile_id,
    })) || [];

  const inventoryTeamsOptions = [
    ...(labsteamData?.data?.[0]?.records || []).map((p: any) => ({
      label: p.name,
      value: p.profile_id,
    })),
    ...backendApprovers,
  ].filter(
    (v, i, a) => a.findIndex((t) => t.value === v.value) === i
  );

  const packingDetails =
    inventoryStatusForm.watch("packingDetails") || [];

  const handleAddMorePackingDetail = () => {
    inventoryStatusForm.setValue("packingDetails", [
      ...packingDetails,
      { packingStatus: "", approvedBy: "", shipmentDate: "", inventoryChecklists: [] },
    ]);
  };

  const getApiPackingStatus = (index: number) =>
    inventoryStatusResp?.data?.inventoryStatusList?.[index]?.packingStatus;

  const checklistRowData = isViewMode ? viewChecklistRows : checklistData;
  const totalChecklistRecords = isViewMode ? checklistRowData?.length : totalRecords;
  /* ------------------------------------------------------------------ */
  /* RENDER */
  /* ------------------------------------------------------------------ */

  return (
    <FormProvider {...inventoryStatusForm}>
      <form onSubmit={inventoryStatusForm.handleSubmit(handleInventorySubmit)}>
        <div className="bg-white rounded-lg px-6 py-6 mt-10">
          {isLoadingExisting ? (
            <div className="text-center py-8 text-gray-500">
              Loading existing data...
            </div>
          ) : (
            <>
              <div className="flex justify-between mb-6">
                <h2 className="text-lg font-semibold text-primary">
                  Inventory Status
                </h2>

                <div className="flex gap-3">
                  {(hasRole(LOGIN_ROLES.INVENTORY_TEAM) ||
                    hasRole(LOGIN_ROLES.SALES_OPS) ||
                    hasRole(LOGIN_ROLES.SUPER_ADMIN)) && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setActiveRowIndex(null)
                          setOpenChecklist(true);
                        }}
                      >
                        Check List
                      </Button>
                    )}

                  {canEditChecklist && (
                    <Button type="submit">
                      Save
                    </Button>
                  )}

                </div>
              </div>

              {packingDetails.map((item: any, index: number) => {
                const apiPackingStatus = getApiPackingStatus(index);
                const isCompleted = apiPackingStatus === taskStatus.Completed.value;

                return <>
                  <div key={index} className="border p-4 rounded-lg mb-4">
                    <div className="grid md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4 items-end">

                      <FormSelect
                        name={`packingDetails.${index}.packingStatus`}
                        label="Packing Status"
                        options={statusOptions}
                        control={inventoryStatusForm.control}
                        isRequired
                        disabled={isCompleted}
                      />

                      <FormSelect
                        name={`packingDetails.${index}.approvedBy`}
                        label="Approved By"
                        options={inventoryTeamsOptions}
                        control={inventoryStatusForm.control}
                        isRequired
                        disabled={isCompleted}
                      />

                      <FormDatePicker
                        name={`packingDetails.${index}.shipmentDate`}
                        label="Shipment Date"
                        control={inventoryStatusForm.control}
                        isRequired
                        disabled={isCompleted}
                      />

                      {!isCompleted && 
                      <>
                        {/* <FormCombobox
                        name={`packingDetails.${index}.inventoryCheckLists`}
                        control={inventoryStatusForm.control}
                        label="Checklists"
                        placeholder="Please enter"
                        isRequired
                          options={checklistData?.map((obj: any) => ({
                            label: obj?.kitName,
                            value: obj?._id,
                            disabled: obj?.included === true,
                          }))}

                          multiSelect
                          disabled={isCompleted}
                          /> */}
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setActiveRowIndex(index);
                              setOpenChecklist(true)
                              setMode("editRow");
                            }}
                          >
                            Check List
                          </Button>
                      </>
                      }


                      <div className={`flex gap-2 ${!isCompleted ? 'col-span-full items-end justify-end' : ''}`}>

                        {!isCompleted &&
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => handleDeleteInventoryStatus(index)}
                          >
                            <Trash2 />
                          </Button>
                        }
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => {
                            setMode("view");
                            setActiveRowIndex(index);
                            setOpenChecklist(true);
                          }}
                        >
                          <EyeIcon />
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              })}

              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="link"
                  onClick={handleAddMorePackingDetail}
                  disabled={!canEditChecklist}
                >
                  + Add More
                </Button>
              </div>

              <FormInput name="note" label="Note" />
            </>
          )}
        </div>

        {/* ---------------- Checklist Dialog ---------------- */}
        <Dialog open={openChecklist} onOpenChange={setOpenChecklist}>
          <DialogContent size="full" className="h-[70vh] bg-white rounded-xl shadow-xl p-3 flex flex-col overflow-hidden">
            {/* Header */}
            <DialogHeader className="flex justify-between items-end border-b px-6 py-4">
              <div className="flex items-center gap-3">
              {<Button onClick={() => activeRowIndex == null ? handleDownload() : handleChecklistDownload()}>Download</Button>}
                <div className="w-[200px]">
                <Select 
                  options={yearOptions()}
                  placeholder="Select Academic Year"
                  onChange={(val: string) => setAcademicYear(val)} />
                  </div>

                {mode === "editRow" &&
                  <Button
                    onClick={() => {
                      inventoryStatusForm.setValue(`packingDetails.${activeRowIndex}.inventoryCheckLists`,
                        Object.entries(draftChecklistState).filter(([_, value]) => value.checked && !value.locked)
                      .map(([key, value]) => ({
                        _id: key,
                        comment: value.comment,
                      })))
                      setOpenChecklist(false);
                      setActiveRowIndex(null);
                    }}
                    variant="default"
                    // disabled={hasRole("SALES_OPS") && !hasRole("SUPER_ADMIN")}
                  >
                    Save
                  </Button>
                }

                <button
                  onClick={() => {setOpenChecklist(false);setActiveRowIndex(null);}}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="size-6 cursor-pointer" />
                </button>
              </div>
            </DialogHeader>

            {/* Table wrapper — fills all available space */}

            <div className="flex-1 overflow-auto border-t border-b">

              <table className="w-full border-collapse table-auto min-w-[1000px]">
                <thead className="bg-[#FFF5E9] text-sm font-semibold sticky top-0 z-10">
                  <tr className="text-primary">
                    <th className="border p-3 text-left min-w-[100px]">Kit Name</th>
                    <th className="border p-3 text-left min-w-[100px]">Kit Type</th>
                    <th className="border p-3 text-left min-w-[180px]">Topic Applicable</th>
                    <th className="border p-3 text-left min-w-[180px]">Academic Year</th>
                    <th className="border p-3 text-center min-w-[120px]">Quantity</th>
                    <th className="border p-3 text-center min-w-[100px]">Included</th>
                    <th className="border p-3 text-center min-w-[150px]">Date of Inclusion</th>
                    <th className="border p-3 text-center min-w-[150px]">Shipment Date</th>
                    <th className="border p-3 text-left min-w-[250px]">Comments</th>
                  </tr>
                </thead>
                <tbody>
                  {checklistRowData?.map(
                    (row: any) => {
                      const state = draftChecklistState[row._id];
                      return (
                        <tr key={row._id} className="hover:bg-gray-50 text-sm text-gray-700">
                          <td className="border p-3">{row.kitName}</td>
                          <td className="border p-3">{row.kitType}</td>
                          <td className="border p-3">{row?.curriculumTopic || ''}</td>
                          <td className="border p-3">{row.academicYear}</td>
                          <td className="border p-3 text-center">{row.quantity}</td>
                          <td className="border p-3 text-center">
                            <input
                              type="checkbox"
                              checked={!!state?.checked}
                              disabled={isViewMode || state?.locked}
                              onChange={(e) =>
                                setDraftChecklistState(prev => ({
                                  ...prev,
                                  [row._id]: {
                                    ...prev[row._id],
                                    checked: e.target.checked,
                                  },
                                }))
                              }

                            />

                          </td>
                          <td className="border p-3 text-center">
                            {row.dateOfInclusion
                              ? new Date(row.dateOfInclusion).toLocaleDateString("en-GB") // formats as DD/MM/YYYY
                              : "-"}
                          </td>
                          <td className="border p-3">{row?.shipmentDate ? moment(row.shipmentDate).format('DD/MM/YYYY') : ''}</td>
                          <td className="border p-3">
                            <input
                              type="text"
                              placeholder="Enter comments"
                              className="w-full border rounded px-2 py-1"
                              value={state?.comment || ""}
                              disabled={isViewMode || state?.locked}
                              onChange={(e) =>
                                setDraftChecklistState((prev) => ({
                                  ...prev,
                                  [row._id]: {
                                    ...prev[row._id],
                                    comment: e.target.value,
                                  },
                                }))
                              }
                            />
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>

              {checklistRowData?.length ? <div className="p-3 flex flex-row items-center justify-between">
                <span>Records: {(page - 1) * limit + 1} -{" "} {Math.min(
                  (page * limit),
                  totalChecklistRecords
                )}{" "}
                of {totalChecklistRecords}</span>
                <div className="flex gap-2">
                  <Button onClick={onPrevBtn} disabled={Number(page) == 1}>{"<"}</Button>
                  <Button onClick={onNextBtn} disabled={Number(page) == Math.ceil(Number(totalChecklistRecords)/Number(limit))}>{">"}</Button>
                </div>
              </div> : <></>}
            </div>
          </DialogContent>
        </Dialog>

        {/* ---------------- Confirm Dialog ---------------- */}
        <Dialog
          open={openConfirmDialog}
          onOpenChange={setOpenConfirmDialog}
        >
          <DialogContent className="max-w-md p-6 rounded-lg bg-white">
            {/* Header */}
            {/* <DialogHeader className="flex justify-between items-end mb-4">
              <button
                onClick={() => setOpenConfirmDialog(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </DialogHeader> */}

            {/* Body */}
            <p className="mb-6">Are you sure you want to save these inventory checklists?</p>
            {/* Footer */}
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpenConfirmDialog(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleUpdateChecklist}
                disabled={updateChecklistMutation.isPending}
              >
                {updateChecklistMutation.isPending ? "Saving..." : "Save"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </form>
    </FormProvider>
  );
};

export default InventoryStatusDetails;
